import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const CallContext = createContext(null);
const ICE = { iceServers: [{ urls: ["stun:stun.l.google.com:19302", "stun:stun1.l.google.com:19302"] }] };

const studentPathFor = (pathname, hash) => {
  const m = pathname.match(/^\/(?:host\/teach\/[^/]+\/day|course\/day)\/([1-7])$/);
  return m ? `/course/day/${m[1]}${hash || ""}` : null;
};

export function CallProvider({ children }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [call, setCall] = useState(null);
  const [incoming, setIncoming] = useState(null);
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [connState, setConnState] = useState("idle");
  const [follow, setFollow] = useState(true);
  const [chat, setChat] = useState([]);
  const pc = useRef(null);
  const streamRef = useRef(null);
  const iceRef = useRef(ICE);
  const after = useRef(0);
  const lastPath = useRef("");
  const isHost = user?.role === "host";

  const send = (id, type, data) => api.post(`/calls/${id}/signal`, { type, data }).catch(() => {});

  const cleanup = useCallback(() => {
    pc.current?.close();
    pc.current = null;
    streamRef.current = null;
    sessionStorage.removeItem("dnn_call");
    setLocalStream((s) => { s?.getTracks().forEach((t) => t.stop()); return null; });
    setRemoteStream(null);
    setCall(null);
    setConnState("idle");
    setChat([]);
    after.current = 0;
    lastPath.current = "";
  }, []);

  const setupPeer = async (c, reuse) => {
    const stream = reuse || (await navigator.mediaDevices.getUserMedia({ video: true, audio: true }));
    streamRef.current = stream;
    setLocalStream(stream);
    try {
      const { data } = await api.get("/calls/ice-servers");
      if (data?.iceServers?.length) iceRef.current = { iceServers: data.iceServers };
    } catch (e) {}
    pc.current?.close();
    const peer = new RTCPeerConnection(iceRef.current);
    stream.getTracks().forEach((t) => peer.addTrack(t, stream));
    peer.ontrack = (e) => setRemoteStream(e.streams[0]);
    peer.onicecandidate = (e) => e.candidate && send(c.id, "ice", e.candidate.toJSON());
    peer.onconnectionstatechange = () => {
      setConnState(peer.connectionState);
      if (peer.connectionState === "failed") {
        if (c.isHostSide) c.offered = false;
        else send(c.id, "rejoin");
      }
    };
    pc.current = peer;
    return peer;
  };

  // Reconnect after a page reload (same tab)
  useEffect(() => {
    const saved = sessionStorage.getItem("dnn_call");
    if (!user || call || !saved) return;
    (async () => {
      try {
        const { data } = await api.get("/calls/active");
        if (!data || data.id !== saved) { sessionStorage.removeItem("dnn_call"); return; }
        const backlog = await api.get(`/calls/${data.id}/signals`, { params: { after: 0 } });
        const last = backlog.data.signals.slice(-1)[0];
        after.current = last ? last.ts : 0;
        const c = { ...data, offered: false, isHostSide: isHost };
        await setupPeer(c);
        setCall(c);
        setConnState(data.status === "ringing" ? "ringing" : "connecting");
        if (!isHost) send(c.id, "rejoin");
        toast.success("Reconnecting your video call...");
      } catch (e) {
        sessionStorage.removeItem("dnn_call");
      }
    })();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const startCall = async (studentId) => {
    try {
      const { data } = await api.post("/calls", { student_id: studentId });
      const c = { ...data, offered: false, isHostSide: true };
      await setupPeer(c);
      setCall(c);
      sessionStorage.setItem("dnn_call", data.id);
      setConnState("ringing");
    } catch (e) {
      toast.error(e.name === "NotAllowedError" ? "Please allow camera and microphone access" : formatApiError(e));
      cleanup();
    }
  };

  const accept = async () => {
    const c = incoming;
    setIncoming(null);
    try {
      await setupPeer(c);
      await api.post(`/calls/${c.id}/accept`);
      setCall(c);
      sessionStorage.setItem("dnn_call", c.id);
      setConnState("connecting");
    } catch (e) {
      toast.error(e.name === "NotAllowedError" ? "Please allow camera and microphone access" : formatApiError(e));
      cleanup();
    }
  };

  const hangUp = async () => {
    const id = call?.id || incoming?.id;
    setIncoming(null);
    if (id) {
      await send(id, "bye");
      await api.post(`/calls/${id}/end`).catch(() => {});
    }
    cleanup();
  };

  // Detect incoming calls (students) when not in a call
  useEffect(() => {
    if (!user || isHost || call || sessionStorage.getItem("dnn_call")) return undefined;
    const check = () => api.get("/calls/active").then(({ data }) => setIncoming(data && data.status === "ringing" ? data : null)).catch(() => {});
    check();
    const t = setInterval(check, 4000);
    return () => clearInterval(t);
  }, [user, isHost, call]);

  // Signaling loop while in a call
  useEffect(() => {
    if (!call) return undefined;
    let stop = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/calls/${call.id}/signals`, { params: { after: after.current } });
        if (stop) return;
        if (data.status === "ended") { toast("Call ended"); cleanup(); return; }
        let peer = pc.current;
        if (isHost && data.status === "active" && !call.offered && peer) {
          call.offered = true;
          const offer = await peer.createOffer({ iceRestart: true });
          await peer.setLocalDescription(offer);
          await send(call.id, "offer", { type: offer.type, sdp: offer.sdp });
          setConnState("connecting");
        }
        for (const s of data.signals) {
          after.current = s.ts;
          if (!peer) continue;
          if (s.type === "rejoin" && isHost) {
            setRemoteStream(null);
            peer = await setupPeer(call, streamRef.current);
            call.offered = false;
            continue;
          }
          if (s.type === "offer") {
            if (peer.remoteDescription) { setRemoteStream(null); peer = await setupPeer(call, streamRef.current); }
            await peer.setRemoteDescription(s.data);
            const ans = await peer.createAnswer();
            await peer.setLocalDescription(ans);
            await send(call.id, "answer", { type: ans.type, sdp: ans.sdp });
          } else if (s.type === "answer") await peer.setRemoteDescription(s.data);
          else if (s.type === "ice") await peer.addIceCandidate(s.data).catch(() => {});
          else if (s.type === "chat") setChat((m) => [...m, { me: false, text: s.data?.text || "", ts: s.ts }]);
          else if (s.type === "bye") { toast("Call ended"); cleanup(); return; }
        }
        if (!isHost && data.current_path && data.current_path !== lastPath.current) {
          lastPath.current = data.current_path;
          const [path, hash] = data.current_path.split("#");
          nav(path);
          if (hash) setTimeout(() => document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "start" }), 900);
        }
      } catch (e) {}
    };
    const t = setInterval(tick, 1200);
    tick();
    return () => { stop = true; clearInterval(t); };
  }, [call, isHost, cleanup, nav]);

  const pushPath = useCallback((path) => {
    if (!call || !isHost || !path) return;
    api.post(`/calls/${call.id}/path`, { path }).catch(() => {});
  }, [call, isHost]);

  // Host: students follow the host's lesson page
  useEffect(() => {
    if (!isHost || !call || !follow) return;
    pushPath(studentPathFor(loc.pathname, ""));
  }, [loc.pathname, isHost, call, follow, pushPath]);

  const showSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    const p = studentPathFor(loc.pathname, `#${id}`);
    if (p) pushPath(p);
  };

  const sendChat = (text) => {
    if (!call || !text.trim()) return;
    setChat((m) => [...m, { me: true, text: text.trim(), ts: Date.now() / 1000 }]);
    send(call.id, "chat", { text: text.trim().slice(0, 2000) });
  };

  return (
    <CallContext.Provider value={{ call, incoming, localStream, remoteStream, connState, follow, setFollow, startCall, accept, hangUp, showSection, isHost, chat, sendChat, canShowSection: !!studentPathFor(loc.pathname, "") }}>
      {children}
    </CallContext.Provider>
  );
}

export const useCall = () => useContext(CallContext);
